/**
 * Prompt Store
 *
 * Manages saved prompt templates and categories.
 * API-backed with Zustand for state management.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import {
  listPromptTemplates,
  createPromptTemplate,
  updatePromptTemplate,
  deletePromptTemplate,
  listPromptCategories,
  createPromptCategory,
  updatePromptCategory,
  deletePromptCategory,
} from '@/lib/api'
import type { PromptTemplate, PromptCategory } from '@/lib/api'

// Re-export types for consumers
export type { PromptTemplate, PromptCategory }

// ===================
// Types
// ===================

interface CreatePromptInput {
  title: string
  content: string
  categoryId?: string | null
  visibility?: 'personal' | 'shared'
  isPinned?: boolean
}

interface UpdatePromptInput {
  title?: string
  content?: string
  categoryId?: string | null
  visibility?: 'personal' | 'shared'
  isPinned?: boolean
}

type VisibilityFilter = 'all' | 'personal' | 'shared'

interface PromptState {
  // Data
  prompts: PromptTemplate[]
  categories: PromptCategory[]
  isLoading: boolean
  error: string | null
  lastFetchedAt: number | null

  // UI filter state
  visibilityFilter: VisibilityFilter
  selectedCategoryId: string | null // null = all, 'uncategorized' = no category

  // Prompt CRUD
  fetchPrompts: (force?: boolean) => Promise<void>
  createPrompt: (data: CreatePromptInput) => Promise<PromptTemplate>
  updatePrompt: (id: string, data: UpdatePromptInput) => Promise<void>
  deletePrompt: (id: string) => Promise<void>
  togglePin: (id: string) => Promise<void>

  // Category CRUD
  fetchCategories: (force?: boolean) => Promise<void>
  createCategory: (name: string) => Promise<PromptCategory>
  updateCategory: (id: string, name: string) => Promise<void>
  deleteCategory: (id: string) => Promise<void>

  // Filters
  setVisibilityFilter: (filter: VisibilityFilter) => void
  setSelectedCategoryId: (categoryId: string | null) => void

  // Computed
  getFilteredPrompts: (searchQuery?: string) => PromptTemplate[]

  // Utility
  clearError: () => void
  reset: () => void
}

// ===================
// Constants
// ===================

const STALE_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

// ===================
// Store
// ===================

// Clean up old localStorage data from the previous client-side implementation
if (typeof window !== 'undefined') {
  localStorage.removeItem('prompt-library')
}

export const usePromptStore = create<PromptState>()(
  devtools(
    (set, get) => ({
      prompts: [],
      categories: [],
      isLoading: false,
      error: null,
      lastFetchedAt: null,
      visibilityFilter: 'all',
      selectedCategoryId: null,

      fetchPrompts: async (force = false) => {
        const { lastFetchedAt, isLoading } = get()
        if (isLoading) return

        if (!force && lastFetchedAt && Date.now() - lastFetchedAt < STALE_THRESHOLD_MS) {
          return
        }

        set({ isLoading: true, error: null })
        try {
          const [prompts, categories] = await Promise.all([
            listPromptTemplates(),
            listPromptCategories(),
          ])
          set({ prompts, categories, lastFetchedAt: Date.now() })
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Failed to load prompts' })
        } finally {
          set({ isLoading: false })
        }
      },

      createPrompt: async (data) => {
        set({ error: null })
        try {
          const prompt = await createPromptTemplate(data)
          set((state) => ({
            prompts: [prompt, ...state.prompts],
            lastFetchedAt: Date.now(),
          }))
          return prompt
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create prompt'
          set({ error: message })
          throw err
        }
      },

      updatePrompt: async (id, data) => {
        set({ error: null })
        try {
          const updated = await updatePromptTemplate(id, data)
          set((state) => ({
            prompts: state.prompts.map((p) => (p.id === id ? updated : p)),
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to update prompt'
          set({ error: message })
          throw err
        }
      },

      deletePrompt: async (id) => {
        set({ error: null })
        try {
          await deletePromptTemplate(id)
          set((state) => ({
            prompts: state.prompts.filter((p) => p.id !== id),
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to delete prompt'
          set({ error: message })
          throw err
        }
      },

      togglePin: async (id) => {
        const prompt = get().prompts.find((p) => p.id === id)
        if (!prompt) return

        const newPinned = !prompt.isPinned
        // Optimistic update
        set((state) => ({
          prompts: state.prompts.map((p) =>
            p.id === id ? { ...p, isPinned: newPinned } : p
          ),
        }))

        try {
          await updatePromptTemplate(id, { isPinned: newPinned })
        } catch (err) {
          // Revert on failure
          set((state) => ({
            prompts: state.prompts.map((p) =>
              p.id === id ? { ...p, isPinned: !newPinned } : p
            ),
            error: err instanceof Error ? err.message : 'Failed to update pin',
          }))
        }
      },

      fetchCategories: async (force = false) => {
        // Categories are fetched alongside prompts in fetchPrompts.
        // This method is for standalone category refresh (e.g., after CRUD).
        if (!force && get().categories.length > 0) return

        try {
          const categories = await listPromptCategories()
          set({ categories })
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Failed to load categories' })
        }
      },

      createCategory: async (name) => {
        set({ error: null })
        try {
          const category = await createPromptCategory({ name })
          // Only add if not already present (idempotent create returns existing)
          set((state) => {
            const exists = state.categories.some((c) => c.id === category.id)
            return exists ? {} : { categories: [...state.categories, category].sort((a, b) => a.name.localeCompare(b.name)) }
          })
          return category
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create category'
          set({ error: message })
          throw err
        }
      },

      updateCategory: async (id, name) => {
        set({ error: null })
        try {
          const updated = await updatePromptCategory(id, { name })
          set((state) => ({
            categories: state.categories
              .map((c) => (c.id === id ? updated : c))
              .sort((a, b) => a.name.localeCompare(b.name)),
            // Update categoryName on prompts that reference this category
            prompts: state.prompts.map((p) =>
              p.categoryId === id ? { ...p, categoryName: updated.name } : p
            ),
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to update category'
          set({ error: message })
          throw err
        }
      },

      deleteCategory: async (id) => {
        set({ error: null })
        try {
          await deletePromptCategory(id)
          set((state) => ({
            categories: state.categories.filter((c) => c.id !== id),
            // Uncategorize prompts that referenced this category
            prompts: state.prompts.map((p) =>
              p.categoryId === id ? { ...p, categoryId: null, categoryName: null } : p
            ),
            // Reset filter if the deleted category was selected
            selectedCategoryId: state.selectedCategoryId === id ? null : state.selectedCategoryId,
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to delete category'
          set({ error: message })
          throw err
        }
      },

      setVisibilityFilter: (filter) => set({ visibilityFilter: filter }),
      setSelectedCategoryId: (categoryId) => set({ selectedCategoryId: categoryId }),

      getFilteredPrompts: (searchQuery = '') => {
        const { prompts, visibilityFilter, selectedCategoryId } = get()

        let filtered = prompts

        // Visibility filter
        if (visibilityFilter !== 'all') {
          filtered = filtered.filter((p) => p.visibility === visibilityFilter)
        }

        // Category filter
        if (selectedCategoryId === 'uncategorized') {
          filtered = filtered.filter((p) => !p.categoryId)
        } else if (selectedCategoryId) {
          filtered = filtered.filter((p) => p.categoryId === selectedCategoryId)
        }

        // Search filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase()
          filtered = filtered.filter(
            (p) =>
              p.title.toLowerCase().includes(query) ||
              p.content.toLowerCase().includes(query)
          )
        }

        return filtered
      },

      clearError: () => set({ error: null }),

      reset: () =>
        set({
          prompts: [],
          categories: [],
          isLoading: false,
          error: null,
          lastFetchedAt: null,
          visibilityFilter: 'all',
          selectedCategoryId: null,
        }),
    }),
    { name: 'prompt-store' },
  ),
)
